import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { FormControl, NonNullableFormBuilder } from "@angular/forms";

import { Observable, Subscription } from "rxjs";
import { filter, take, map } from "rxjs/operators";

import {
  E911Service,
  E911Location
} from "../../../../../../../common/services/api/resources/e911/e911.service";
import { UserService } from "../../../../../../../common/services/api/resources/user/user.service";
import { UserAddressService } from "../../../../../../../common/services/api/resources/userAddress/user-address.service";
import { UserAddressE911Info } from "../../e911-table.component";
import { TranslateService } from "@ngx-translate/core";
import { LoadingModalComponent } from "../../../../../shared/components/loadingModal/loading-modal.component";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { AdminE911BaseFormComponent } from "../../../e911-base-form.component";
import { listOfStates } from "../../../../../../../common/interfaces/state";
import { E911LocationType } from "@onsip/common/services/api/resources/e911/e911-location";

@Component({
  selector: "onsip-admin-e911-table-shelf-edit",
  templateUrl: "./e911-table-shelf-edit.component.html",
  styleUrls: ["./e911-table-shelf-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminE911TableShelfEditComponent
  extends AdminE911BaseFormComponent
  implements OnInit, OnDestroy
{
  @Input() e911Location!: E911Location;
  locationPrivate: E911LocationType = "Private";
  @Input() userAddressE911Info!: Observable<Array<UserAddressE911Info>>;
  @Output() viewPanel = new EventEmitter<void>();

  userE911InfoArray: Array<UserAddressE911Info> = [];

  listOfStates = listOfStates.sort(([, symbolA], [, symbolB]) => (symbolA < symbolB ? -1 : 1));

  isMediumView = this.breakpointObserver
    .observe(Breakpoints.Medium)
    .pipe(map(result => result.matches));

  /** hidden form field solely used for autocompleting user's state */
  autoCompleteState = new FormControl();

  protected unsubscriber = new Subscription();

  constructor(
    protected translate: TranslateService,
    protected fb: NonNullableFormBuilder,
    private e911Service: E911Service,
    private userService: UserService,
    private userAddressService: UserAddressService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private snackbar: SnackbarService
  ) {
    super(translate, fb);
  }

  ngOnInit(): void {
    this.additionalParams.addControl("assignedUsers", new FormControl([]));
    this.e911FormGroup.setValue({
      name: this.e911Location.locationName,
      address: this.e911Location.streetNumber + " " + this.e911Location.streetName,
      secondaryAddressType: this.e911Location.addressType,
      secondaryAddressNumber: this.e911Location.addressTypeNumber,
      city: this.e911Location.city,
      state: this.e911Location.state,
      zipcode: this.e911Location.zipcode,
      email: this.e911Location.notificationEmail || "",
      additionalParams: {
        assignedUsers: []
      }
    });

    this.unsubscriber.add(
      this.userAddressE911Info.subscribe(userE911Info => {
        if (this.e911Location.locationType === "Shared") {
          this.userE911InfoArray = userE911Info;
        } else if (this.e911Location.locationType === "Private") {
          // only show the user who is assigned to this private location
          this.userE911InfoArray = userE911Info.filter(
            user => user.userId === this.e911Location.userId
          );
        }
        this.additionalParams.controls.assignedUsers.setValue(
          userE911Info.filter(users => users.e911LocationId === this.e911Location.e911LocationId)
        );
      })
    );

    this.unsubscriber.add(
      this.autoCompleteState.valueChanges.subscribe(value => {
        this.e911FormGroup.controls.state.setValue(value);
      })
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  togglePanel(): void {
    this.viewPanel.next();
  }

  save(): void {
    if (this.e911FormGroup.valid) {
      // start loading modal
      this.loadingModal = this.dialog.open(LoadingModalComponent);

      // splitting address by the first space to get street number and name for api
      const e911Address: string = this.e911FormGroup.controls.address.value;
      const StreetNumber = e911Address.substring(0, e911Address.indexOf(" "));
      const StreetName = e911Address.substring(e911Address.indexOf(" ") + 1);
      this.currentE911Params = {
        AddressType: this.e911FormGroup.controls.secondaryAddressType.value,
        AddressTypeNumber: this.e911FormGroup.controls.secondaryAddressNumber.value,
        City: this.e911FormGroup.controls.city.value,
        Country: this.e911Location.country,
        E911LocationId: this.e911Location.e911LocationId,
        LocationName: this.e911FormGroup.controls.name.value,
        NotificationEmail: this.e911FormGroup.controls.email.value,
        State: this.e911FormGroup.controls.state.value,
        StreetName,
        StreetNumber,
        Zipcode: this.e911FormGroup.controls.zipcode.value
      };

      this.e911Service.e911LocationEdit(this.currentE911Params);

      this.unsubscriber.add(
        this.e911Service.state
          .pipe(
            filter(state => !state.loading),
            map(state => state.errors),
            take(1)
          )
          .subscribe(errors => {
            // turn on api error flag if there are any
            // this will show the contact support error message on top of the component
            this.hasApiError = errors.length > 0;
            if (!errors.length) {
              // handle assigning and deassigning of users
              this.handledAssigningSharedLocation();
            } else {
              this.snackbar.openSnackBar(
                this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION"),
                "error"
              );
              this.handleApiErrors(errors[0]);
              this.e911Service.clearErrors();
              // close loading modal
              this.loadingModal.close();
            }
            this.cdRef.markForCheck();
          })
      );
    }
  }

  private handledAssigningSharedLocation(): void {
    const pendingAssignedUsers: Array<UserAddressE911Info> =
      this.additionalParams.controls.assignedUsers.value;
    const currentAssignedUsers = this.userE911InfoArray.filter(
      e911Info => e911Info.e911LocationId === this.e911Location.e911LocationId
    );
    // filter out for newly added users
    const newlyAssignedUsers = pendingAssignedUsers.filter(
      pAssignedUser => !currentAssignedUsers.find(curr => pAssignedUser.name === curr.name)
    );

    // get deselected users
    const removedAssignedUsers = currentAssignedUsers.filter(
      cAssignedUser => !pendingAssignedUsers.find(pending => cAssignedUser.name === pending.name)
    );

    // Check if there is any change in assigned users before calling userAddressBrowse
    // If we dont need to make any changes, then dont need to call the api and just go straight to view panel
    if (newlyAssignedUsers.length === 0 && removedAssignedUsers.length === 0) {
      this.togglePanel();
      this.loadingModal.close();
      this.snackbar.openSnackBar(
        this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
        "success"
      );
      return;
    }

    Promise.all(
      newlyAssignedUsers
        .map(user => {
          if (user.e911Provisioning) {
            return this.e911Service.e911LocationAssign({
              Address: user.aor,
              E911LocationId: this.e911Location.e911LocationId
            });
          } else {
            return this.userService
              .userEditE911Provisioning({ Enabled: true, UserId: user.userId })
              .then(result => {
                if (result.status === "success") {
                  return this.e911Service.e911LocationAssign({
                    Address: user.aor,
                    E911LocationId: this.e911Location.e911LocationId
                  });
                } else {
                  this.snackbar.openSnackBar(
                    `Could not provision E911 to ${user.name}. Please try again.`,
                    "error"
                  );
                  this.userService.clearErrors();
                  return Promise.reject(result.data.message);
                }
              });
          }
        })
        .concat(
          removedAssignedUsers.map(user => {
            return this.e911Service.e911LocationDeassign(user.aor);
          })
        )
    ).then(async results => {
      if (results.every(res => res.status === "success")) {
        await this.e911Service.e911LocationBrowse().then(result => {
          if (result.status === "error") {
            this.snackbar.openSnackBar(
              "Could not update users with new E911 locations. Please refresh the app to see updated lcations.",
              "success"
            );
          }
        });
        this.userAddressService.userAddressBrowseWithOrgId({ Limit: 5000 }).then(result => {
          if (result.status === "success") {
            this.togglePanel();
            this.loadingModal.close();
            this.snackbar.openSnackBar(
              this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
              "success"
            );
          } else {
            this.snackbar.openSnackBar(
              "Could not update users with new E911 locations. Please try again",
              "error"
            );
            this.userAddressService.clearErrors();
          }
        });
      } else {
        this.snackbar.openSnackBar(
          "Could not assign users to given E911 location. Please try again",
          "error"
        );
        this.e911Service.clearErrors();
      }
    });
  }
}
